/**
 * Drizzle ORM helper — converts BranchScopeCondition[] into SQL WHERE clauses.
 *
 * Usage in route handlers:
 *   const branchFilter = applyBranchScope(rentalBookings, doctypeBranchScope, context);
 *   if (branchFilter) conditions.push(branchFilter);
 */

import { eq, or, inArray, and, type SQL } from "drizzle-orm";
import {
  buildBranchScopeConditions,
  type BranchScopeConfig,
  type BranchScopeContext,
} from "./branch-scope";

/**
 * Apply branch scope conditions to a Drizzle query.
 *
 * For "single" type: AND logic (all conditions must match)
 * For "multi-field" type: OR across fields within each layer, AND between layers
 *   - Layer 1 (permission): field1 IN userBranches OR field2 IN userBranches
 *   - Layer 2 (view filter): field1 = activeBranch OR field2 = activeBranch
 *   - Final: Layer1 AND Layer2
 *
 * @param table  Drizzle table reference (e.g., rentalBookings)
 * @param config BranchScopeConfig from the module's doctype definition
 * @param context User's branch context (allowed branches + active selection)
 * @returns SQL condition to add to .where(), or undefined if no filtering needed
 */
export function applyBranchScope(
  table: Record<string, any>,
  config: BranchScopeConfig | undefined,
  context: BranchScopeContext,
): SQL | undefined {
  const conditions = buildBranchScopeConditions(config, context);
  if (conditions.length === 0) return undefined;

  // For "single" type: all conditions are AND-ed
  if (!config || config.type === "single") {
    const sqlConditions = conditions
      .map((c) => {
        const column = table[c.field];
        if (!column) return undefined;
        if (c.operator === "eq") return eq(column, c.value as string);
        if (c.operator === "in") {
          const values = c.value as string[] | undefined;
          if (!Array.isArray(values) || values.length === 0) return undefined;
          return inArray(column, values);
        }
        return undefined;
      })
      .filter((x): x is SQL => x !== undefined);

    if (sqlConditions.length === 0) return undefined;
    return and(...sqlConditions);
  }

  // For "multi-field" type: group by operator into layers, OR within layer, AND between layers
  if (config.type === "multi-field") {
    const layers: SQL[] = [];

    // Permission layer — "in" conditions OR-ed together
    const permConditions = conditions
      .filter((c) => c.operator === "in")
      .map((c) => {
        const column = table[c.field];
        const values = c.value as string[] | undefined;
        if (!column || !Array.isArray(values) || values.length === 0) return undefined;
        return inArray(column, values);
      })
      .filter((x): x is SQL => x !== undefined);

    if (permConditions.length > 0) {
      layers.push(permConditions.length === 1 ? permConditions[0] : or(...permConditions)!);
    }

    // View filter layer — "eq" conditions OR-ed together
    const viewConditions = conditions
      .filter((c) => c.operator === "eq")
      .map((c) => {
        const column = table[c.field];
        if (!column) return undefined;
        return eq(column, c.value as string);
      })
      .filter((x): x is SQL => x !== undefined);

    if (viewConditions.length > 0) {
      layers.push(viewConditions.length === 1 ? viewConditions[0] : or(...viewConditions)!);
    }

    if (layers.length === 0) return undefined;
    return layers.length === 1 ? layers[0] : and(...layers);
  }

  return undefined;
}
